import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FEATURES = [
  { icon: '🤖', titleKey: 'landing.feature1Title', descKey: 'landing.feature1Desc' },
  { icon: '📱', titleKey: 'landing.feature2Title', descKey: 'landing.feature2Desc' },
  { icon: '📊', titleKey: 'landing.feature3Title', descKey: 'landing.feature3Desc' },
  { icon: '🔌', titleKey: 'landing.feature4Title', descKey: 'landing.feature4Desc' },
];

const TIERS = [
  { name: 'Demo', price: 'Free', period: '4 hours', features: ['1 Career Mundus', '3 AI agents', 'WhatsApp connection', 'Full access'], cta: 'Start Free Demo', primary: false },
  { name: 'Pro', price: '$19.99', period: '/month', features: ['3 Munduses (Home, Career, Relationships)', '9 AI agents', 'All channels', 'Email reports', 'Plugin integrations'], cta: 'Get Pro', primary: true },
  { name: 'Team', price: '$49.99', period: '/month', features: ['10 Munduses (Supply Chain)', '30 AI agents', '5 team seats', 'Priority support', 'API access'], cta: 'Get Team', primary: false },
];

export default function Landing() {
  const { t, i18n } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Mundus Novus
        </div>
        <div className="flex items-center gap-4">
          <select
            value={i18n.language}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-300"
          >
            <option value="en">EN</option>
            <option value="es">ES</option>
            <option value="pt-BR">PT</option>
            <option value="fr">FR</option>
          </select>
          <Link to="/login" className="text-slate-300 hover:text-white transition-colors">
            {t('auth.login')}
          </Link>
          <Link to="/register" className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg hover:opacity-90 transition-opacity font-medium">
            {t('auth.register')}
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 pt-20 pb-32 max-w-5xl mx-auto text-center">
        <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
          {t('landing.heroTitle', 'Your AI agents work')}
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            {t('landing.heroHighlight', 'while you live.')}
          </span>
        </h1>
        <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
          {t('landing.heroSub', 'Autonomous AI agents that plan your day, track your goals, and report to you on WhatsApp — 24/7.')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/register" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl text-lg font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-blue-500/25">
            {t('landing.cta', 'Start Free Demo')}
          </Link>
          <a href="https://wa.me/573102197789?text=hola" target="_blank" rel="noopener noreferrer" className="px-8 py-4 border border-green-500/50 text-green-400 rounded-xl text-lg font-semibold hover:bg-green-500/10 transition-colors flex items-center justify-center gap-2">
            <span>💬</span> {t('landing.whatsappCta', 'Try on WhatsApp')}
          </a>
        </div>
        <p className="text-sm text-slate-500 mt-4">
          {t('landing.ctaSub', '4 hours free. No credit card. No app to install.')}
        </p>
      </section>

      {/* How It Works */}
      <section className="px-6 py-20 max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-16">
          {t('landing.howTitle', 'How it works')}
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { step: '1', icon: '🎯', title: t('landing.step1Title', 'Create Your Mundus'), desc: t('landing.step1Desc', 'Choose your goal — Career, Home, or Relationships. AI creates your agent team instantly.') },
            { step: '2', icon: '📱', title: t('landing.step2Title', 'Connect WhatsApp'), desc: t('landing.step2Desc', 'Link your WhatsApp in 30 seconds. Agents send plans and insights where you already are.') },
            { step: '3', icon: '🚀', title: t('landing.step3Title', 'Live Your Life'), desc: t('landing.step3Desc', 'Agents work autonomously 24/7. Approve plans with "approve". Get summaries while you relax.') },
          ].map((s) => (
            <div key={s.step} className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-2xl flex items-center justify-center text-3xl">
                {s.icon}
              </div>
              <div className="text-sm text-blue-400 font-mono mb-2">Step {s.step}</div>
              <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-slate-400">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Life Pillars */}
      <section className="px-6 py-20 bg-slate-800/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">
            {t('landing.pillarsTitle', 'The 3 Life Pillars')}
          </h2>
          <p className="text-slate-400 text-center mb-16 max-w-2xl mx-auto">
            {t('landing.pillarsSub', 'Pro plan gives you 3 Munduses — one for each domain of your life.')}
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '🏠', title: t('landing.pillar1', 'Home Manager'), desc: t('landing.pillar1Desc', 'Automate groceries, track expenses, coordinate household tasks, manage bills.') },
              { icon: '🚀', title: t('landing.pillar2', 'Career Accelerator'), desc: t('landing.pillar2Desc', 'Daily growth tasks, industry trends, skill tracking, opportunity alerts.') },
              { icon: '❤️', title: t('landing.pillar3', 'Relationship Coach'), desc: t('landing.pillar3Desc', 'Quality time tracking, date ideas, important dates, communication coaching.') },
            ].map((p) => (
              <div key={p.title} className="p-6 bg-slate-900/50 rounded-2xl border border-slate-700 hover:border-blue-500/50 transition-colors">
                <div className="text-4xl mb-4">{p.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{p.title}</h3>
                <p className="text-slate-400 text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-6 py-20 max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-16">
          {t('landing.pricingTitle', 'Simple pricing')}
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {TIERS.map((tier) => (
            <div key={tier.name} className={`p-8 rounded-2xl border ${tier.primary ? 'border-blue-500 bg-blue-500/5 shadow-lg shadow-blue-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
              <h3 className="text-xl font-semibold mb-1">{tier.name}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold">{tier.price}</span>
                <span className="text-slate-400">{tier.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/register" className={`block text-center py-3 rounded-lg font-medium transition-opacity hover:opacity-90 ${tier.primary ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'bg-slate-700 text-slate-300'}`}>
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="px-6 py-20 bg-gradient-to-r from-green-900/30 to-emerald-900/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            {t('landing.whatsappTitle', 'Why WhatsApp-first?')}
          </h2>
          <p className="text-xl text-slate-400 mb-8">
            {t('landing.whatsappSub', '98% open rate. 3 billion users. Zero app fatigue. Your agents live where you already are.')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            {['WhatsApp', 'Telegram', 'Discord', 'Email'].map((ch) => (
              <div key={ch} className="p-4 bg-slate-800/50 rounded-xl text-center">
                <div className="text-sm text-slate-400">{ch}</div>
              </div>
            ))}
          </div>
          <a href="https://wa.me/573102197789?text=hola" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 rounded-xl text-lg font-semibold hover:bg-green-700 transition-colors">
            💬 {t('landing.tryWhatsApp', 'Message us on WhatsApp')}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 border-t border-slate-800">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-slate-500 text-sm">
            Mundus Novus {new Date().getFullYear()}. {t('landing.footer', 'Your AI agents work while you live.')}
          </div>
          <div className="flex gap-6 text-sm text-slate-500">
            <Link to="/login" className="hover:text-white transition-colors">{t('auth.login')}</Link>
            <Link to="/register" className="hover:text-white transition-colors">{t('auth.register')}</Link>
            <a href="https://wa.me/573102197789" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
